import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import {
  ZIGGY_VARIANTS,
  ZIGGY_VARIANT_META,
  ziggySrc,
  type ZiggyAge,
  type ZiggyVariant,
} from "../lib/ziggy";
import { useZiggyVariant } from "../lib/ziggyContext";
import { useSetZiggyVariant } from "../lib/useZiggyPersistence";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { useLearningPreferences } from "../lib/learning/hooks";
import { useSession } from "../lib/sessionContext";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/**
 * One-time "meet Ziggy" chooser. Appears the first time a learner opens the
 * portal (until they pick). Previews both looks at the learner's own age so
 * they see their real buddy; the signature daisy is part of every render.
 */
export function ZiggyChooser({ age }: { age: ZiggyAge }) {
  const reduce = useReducedMotion();
  const { chosen } = useZiggyVariant();
  const setVariant = useSetZiggyVariant();
  const { strings: s } = usePortalLocale();
  const { user } = useSession();
  const prefs = useLearningPreferences(Boolean(user));
  // A fresh browser has no local pick; wait for the server so a learner who
  // already chose on another device never sees the chooser flash.
  const serverHasPick = Boolean(prefs.data?.ziggy_variant);
  const open = !chosen && !prefs.isPending && !serverHasPick;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ziggy-chooser-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/45 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
            className="border-border/60 bg-card w-full max-w-lg rounded-[1.75rem] border p-6 shadow-[var(--shadow-float)] sm:p-8"
          >
            <div className="text-center">
              <p className="text-primary text-xs font-semibold uppercase tracking-wide">
                {s.ziggyChooserEyebrow}
              </p>
              <h2
                id="ziggy-chooser-title"
                className="font-display mt-1 text-2xl font-semibold leading-tight"
              >
                {s.ziggyChooserTitle}
              </h2>
              <p className="text-muted-foreground mt-1.5 text-sm">{s.ziggyChooserBlurb}</p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4">
              {ZIGGY_VARIANTS.map((variant) => (
                <VariantOption
                  key={variant}
                  variant={variant}
                  age={age}
                  onPick={() => setVariant(variant)}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function VariantOption({
  variant,
  age,
  onPick,
}: {
  variant: ZiggyVariant;
  age: ZiggyAge;
  onPick: () => void;
}) {
  const meta = ZIGGY_VARIANT_META[variant];
  const { strings: s } = usePortalLocale();
  const blurb = variant === "pink" ? s.ziggyBlurbPink : s.ziggyBlurbBlue;
  return (
    <button
      type="button"
      onClick={onPick}
      aria-label={s.ziggyChooseAria(meta.label)}
      className={cn(
        "border-border/70 bg-muted/30 group relative flex flex-col items-center gap-2 rounded-[1.35rem] border-2 p-4 text-center transition-all duration-150 ease-out",
        "hover:border-primary/70 hover:bg-primary/5 hover:-translate-y-0.5 active:scale-[0.98]",
      )}
    >
      <span
        aria-hidden
        className="absolute inset-x-6 bottom-3 h-4 rounded-full blur-xl"
        style={{ background: `color-mix(in oklch, ${meta.swatch} 45%, transparent)` }}
      />
      <img
        src={ziggySrc(variant, age, "happy")}
        alt=""
        aria-hidden
        loading="eager"
        draggable={false}
        className="relative h-40 w-auto object-contain drop-shadow-[0_12px_20px_rgba(30,20,60,0.28)] transition-transform duration-200 ease-out group-hover:scale-[1.04]"
      />
      <span
        aria-hidden
        className="size-3 rounded-full ring-2 ring-white/70"
        style={{ background: meta.swatch }}
      />
      <span className="font-display text-base font-semibold leading-none">{meta.label}</span>
      <span className="text-muted-foreground text-xs">{blurb}</span>
      <span className="text-primary mt-1 inline-flex items-center gap-1 text-xs font-semibold opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        <Check className="size-3.5" /> {s.ziggyChoose}
      </span>
    </button>
  );
}
